import React from 'react';
import { Link } from 'react-router-dom';
import { Display, Meta, Button } from './Atoms';
import { Sprout, EmptyBed, EmptyJournal, GrowingSeedling } from './Illustrations';

/**
 * Wspólne komponenty stanów - loading, error, empty, login wymagany.
 * Wszystkie w stylu Terroir.
 */

export function LoadingState({ label = 'Wczytuję…' }) {
  return (
    <div style={{
      display: 'grid', placeItems: 'center',
      minHeight: '40vh', padding: '40px 24px',
    }}>
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14,
      }}>
        <GrowingSeedling size={64} loop />
        <Meta>{label}</Meta>
      </div>
    </div>
  );
}

export function ErrorState({ error, onRetry }) {
  const isAuth = error?.status === 401 || error?.status === 403;
  if (isAuth) return <NeedsLoginState />;

  return (
    <div style={{
      display: 'grid', placeItems: 'center',
      minHeight: '40vh', padding: '40px 24px',
    }}>
      <div style={{ maxWidth: 360, textAlign: 'center' }}>
        <Display size={28} style={{ display: 'block', marginBottom: 12 }}>
          Coś nie zadziałało
        </Display>
        <div style={{
          fontSize: 14, color: 'var(--t-text-dim)',
          marginBottom: 20, lineHeight: 1.5,
        }}>
          {error?.message || 'Nieznany błąd'}
          {error?.status && (
            <Meta style={{ marginTop: 8 }}>HTTP {error.status}</Meta>
          )}
        </div>
        {onRetry && (
          <Button onClick={onRetry}>Spróbuj ponownie</Button>
        )}
      </div>
    </div>
  );
}

export function NeedsLoginState() {
  return (
    <div style={{
      display: 'grid', placeItems: 'center',
      minHeight: '60vh', padding: '40px 24px',
    }}>
      <div style={{ maxWidth: 420, textAlign: 'center' }}>
        <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'center' }}>
          <Sprout size={80} color="var(--t-sage)" />
        </div>
        <Display size={36} style={{
          display: 'block', marginBottom: 14, letterSpacing: '-.03em',
        }}>
          Potrzebne <span style={{ color: 'var(--t-sage)', fontStyle: 'italic' }}>logowanie</span>
        </Display>
        <div style={{
          fontSize: 15, color: 'var(--t-text-dim)',
          marginBottom: 28, lineHeight: 1.55,
        }}>
          Zaloguj się żeby zobaczyć swój ogród w GardenApp.pl.
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <Link to="/login" style={{ textDecoration: 'none' }}>
            <Button>Zaloguj się</Button>
          </Link>
        </div>
        <Meta style={{ marginTop: 22, opacity: 0.6 }}>
          konto demo: test@test.pl · Test@test1
        </Meta>
      </div>
    </div>
  );
}

export function EmptyState({ illustration = 'bed', title, description, action }) {
  const Ill = illustration === 'journal' ? EmptyJournal : EmptyBed;
  return (
    <div style={{
      display: 'grid', placeItems: 'center',
      padding: '60px 24px',
    }}>
      <div style={{ maxWidth: 360, textAlign: 'center' }}>
        <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'center' }}>
          <Ill size={140} color="var(--t-text-mute)" />
        </div>
        <Display size={24} style={{ display: 'block', marginBottom: 10 }}>{title}</Display>
        {description && (
          <div style={{
            fontSize: 14, color: 'var(--t-text-dim)',
            marginBottom: 20, lineHeight: 1.5,
          }}>{description}</div>
        )}
        {action}
      </div>
    </div>
  );
}

/**
 * TodaySkeleton — page-level placeholder dla Today (najczęściej odwiedzanej).
 * Symuluje docelowy layout: hero data + 3 zadania + carousel obserwuj.
 * Mniejszy layout-shift niż samo "Wczytuję…" pod centrum strony.
 */
export function TodaySkeleton() {
  return (
    <div style={{ padding: '32px 40px 60px', maxWidth: 1200 }}>
      <div style={{ marginBottom: 28 }}>
        <Skeleton width={120} height={11} style={{ marginBottom: 10 }} />
        <Skeleton width="60%" height={48} style={{ marginBottom: 8 }} />
        <Skeleton width="35%" height={14} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 32 }}>
        <div>
          {[0, 1, 2].map(i => (
            <div key={i} style={{
              padding: 18, marginBottom: 12,
              border: '1px solid var(--t-line)', borderRadius: 12,
            }}>
              <Skeleton width="40%" height={11} style={{ marginBottom: 8 }} />
              <Skeleton width="80%" height={20} style={{ marginBottom: 6 }} />
              <Skeleton width="50%" height={13} />
            </div>
          ))}
        </div>
        <div>
          <Skeleton width="100%" height={140} style={{ borderRadius: 14, marginBottom: 16 }} />
          <Skeleton width="100%" height={120} style={{ borderRadius: 14 }} />
        </div>
      </div>
    </div>
  );
}

/**
 * PlantsGridSkeleton — placeholder dla siatki kart roślin (Plants page).
 * Reaguje na desktop/mobile szerokość.
 */
export function PlantsGridSkeleton({ count = 12 }) {
  return (
    <div style={{ padding: '32px 40px 60px' }}>
      <div style={{ marginBottom: 28, paddingBottom: 18, borderBottom: '1px solid var(--t-line)' }}>
        <Skeleton width={140} height={11} style={{ marginBottom: 10 }} />
        <Skeleton width="50%" height={48} />
      </div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 24, flexWrap: 'wrap' }}>
        {[0, 1, 2, 3].map(i => (
          <Skeleton key={i} width={90} height={36} style={{ borderRadius: 999 }} />
        ))}
      </div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: 16,
      }}>
        {Array.from({ length: count }).map((_, i) => (
          <div key={i} style={{
            borderRadius: 16, overflow: 'hidden',
            border: '1px solid var(--t-line)',
          }}>
            <Skeleton width="100%" height={140} style={{ borderRadius: 0 }} />
            <div style={{ padding: '14px 16px' }}>
              <Skeleton width="80%" height={18} style={{ marginBottom: 8 }} />
              <Skeleton width="55%" height={11} style={{ marginBottom: 12 }} />
              <Skeleton width="40%" height={11} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Skeleton block - placeholder gdy ładujemy dane.
 */
export function Skeleton({ width = '100%', height = 16, style }) {
  return (
    <div style={{
      width, height,
      borderRadius: 6,
      background: 'linear-gradient(90deg, var(--t-bg-elev) 0%, var(--t-bg-elev-2) 50%, var(--t-bg-elev) 100%)',
      backgroundSize: '200% 100%',
      animation: 'skeleton-shimmer 1.5s ease-in-out infinite',
      ...style,
    }}>
      <style>{`
        @keyframes skeleton-shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </div>
  );
}
